import { useParams, Link, Navigate } from "react-router-dom";
import { Check, ArrowLeft } from "lucide-react";
import { SERVICES } from "@/data/site";
import { Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useSeo } from "@/hooks/useSeo";

const SERVICE_SEO = {
  "roof-cleaning": {
    title: "Roof Cleaning Lancashire | Moss Removal & Treatment | StoneCrest",
    description: "Professional roof cleaning across Lancashire, including moss removal and treatments for organic growth. Careful cleaning methods for homes and commercial properties. Free fixed-price quotes.",
  },
  "render-softwashing": {
    title: "Render Cleaning Lancashire | Professional Softwashing | StoneCrest",
    description: "Professional render cleaning and softwashing across Lancashire for algae, organic staining and weathered exterior walls. Fully insured with free fixed-price quotes.",
  },
  "driveway-patio-cleaning": {
    title: "Driveway Cleaning Lancashire | Patio & Paving Cleaning | StoneCrest",
    description: "Professional driveway, patio and paving cleaning across Lancashire for block paving, concrete and suitable stone surfaces. Fully insured with free fixed-price quotes.",
  },
  "gutter-clearing": {
    title: "Gutter Cleaning Lancashire | Professional Gutter Clearing | StoneCrest",
    description: "Professional gutter clearing across Lancashire for homes, landlords and commercial properties. Remove leaves, moss and debris with fully insured service and free quotes.",
  },
  "fascia-soffit-cleaning": {
    title: "Fascia & Soffit Cleaning Lancashire | StoneCrest",
    description: "Professional fascia and soffit cleaning across Lancashire to remove dirt, algae and weather staining from exterior roofline surfaces. Fully insured with free quotes.",
  },
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const service = SERVICES.find((s) => s.slug === slug);
  const idx = SERVICES.findIndex((s) => s.slug === slug);
  const seo = SERVICE_SEO[slug];
  useSeo(
    seo ? seo.title : service ? `${service.name} in Lancashire | StoneCrest Exterior Specialists` : "Service | StoneCrest",
    seo ? seo.description : service ? `${service.name} across Lancashire. ${service.tagline}. Fully insured, premium finish. Free fixed-price quotes from StoneCrest Exterior Specialists.` : "",
    service ? `https://stonecrestexteriors.co.uk/services/${slug}` : undefined
  );
  if (!service) return <Navigate to="/services" replace />;

  return (
    <div>
      <section className="grid lg:grid-cols-2">
        <div className="px-6 md:px-12 lg:pl-12 lg:pr-16 py-16 md:py-24 flex flex-col justify-center max-w-3xl lg:ml-auto lg:w-full">
          <Link to="/services" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--muted)] hover:text-[var(--oxblood)] transition-colors mb-8"><ArrowLeft className="w-4 h-4" /> All Services</Link>
          <p className="font-display text-sm text-[var(--oxblood)] mb-3">Service {String(idx + 1).padStart(2, "0")}</p>
          <h1 className="font-serif font-light text-5xl md:text-6xl leading-tight" data-testid="service-title">{service.name}</h1>
          <p className="mt-6 text-[var(--muted)] text-lg leading-relaxed">{service.description}</p>
          <p className="mt-4 text-[var(--muted)] text-sm">Available for homes and businesses right across <Link to="/areas/lancashire" className="text-[var(--oxblood)] underline underline-offset-2 hover:text-[var(--oxblood-hover)] transition-colors" data-testid="service-lancashire-link">Lancashire</Link>.</p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/quote" data-testid="service-quote-btn" className="bg-[var(--oxblood)] text-white px-8 py-4 tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors text-center">Get a Free Quote</Link>
          </div>
        </div>
        <div className="relative min-h-[50vh] lg:min-h-[80vh] overflow-hidden">
          <img src={service.image} alt={service.name} className="absolute inset-0 w-full h-full object-cover" />
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-[var(--bone)]">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-24 md:py-28 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">Benefits</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">Why it matters.</h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6 grid sm:grid-cols-2 gap-x-10 gap-y-6">
            {service.benefits.map((b, i) => (
              <Reveal key={i} delay={i * 0.05} className="flex items-start gap-4 border-t border-[var(--border)] pt-5">
                <Check className="w-5 h-5 text-[var(--oxblood)] mt-0.5 shrink-0" />
                <span className="text-[var(--ink)]">{b}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-6 md:px-12 max-w-4xl mx-auto py-24 md:py-28">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">FAQ</p>
        <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight mb-10">{service.name} questions.</h2>
        <Accordion type="single" collapsible className="w-full">
          {service.faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="border-b border-[var(--border)]" data-testid={`service-faq-${i}`}>
              <AccordionTrigger className="text-left font-serif text-xl md:text-2xl py-6 hover:no-underline hover:text-[var(--oxblood)]">{f.q}</AccordionTrigger>
              <AccordionContent className="text-[var(--muted)] text-base leading-relaxed pb-6">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <CTASection title={`Book your ${service.name.toLowerCase()} today.`} />
    </div>
  );
}
